import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";
import { useEffect, useState } from "react";
import { Table, Button, Input, Space, Tag, Popconfirm, message } from "antd";
import { PlusOutlined, EditOutlined, DeleteOutlined } from "@ant-design/icons";
import QuickModal from "./Modal";
import Layout from '../../Layout'
import {
    Edit,
    X,
} from "lucide-react";
import { mixDelV1, mixGetV1, mixPostV1, mixPutV1 } from "services/mixin";
import useAuthStore from "../../store/authStore";
import useTableColumnSettings, { SettingColumn } from "../../hooks/useTableColumnSettings";
import { ColumnSettingButton } from "../../components/TableColumnSetting";

const { Search } = Input;

interface TagItem {
    title: string;
    is_active: boolean;
    position: number;
}

export default function CustomerTags() {
  const { t } = useTranslation();
  // Phân quyền thao tác: chỉ có quyền xem thì không hiện nút thêm/sửa/xóa
  const { isSuperAdmin, hasPermission } = useAuthStore();
  const canCreate = isSuperAdmin || hasPermission("quick_messages.create");
  const canEdit = isSuperAdmin || hasPermission("quick_messages.edit");
  const canDelete = isSuperAdmin || hasPermission("quick_messages.delete");
    const [searchTerm, setSearchTerm] = useState("");
    const [modalOpen, setModalOpen] = useState(false);
    const [mode, setMode] = useState<"add" | "edit">("add");
    const [editingItem, setEditingItem] = useState<TagItem | null>(null);

    const [data, setData] = useState<TagItem[]>([]);
    const [page, setPage] = useState(1);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(false);
    const [sortParam, setSortParam] = useState<string>("position,asc");

    const [actionModal, setActionModal] = useState<{ open: boolean; type: "approve" | "reject" | null; id: number | null }>({ open: false, type: null, id: null });

    const openDeleteModal = (id: number) => {
        setActionModal({
            open: true,
            type: "reject",
            id,
        });
    };

    const handleCloseActionModal = () => {
        setActionModal({
            open: false,
            type: null,
            id: null,
        });
    };

    const handleConfirmAction = async () => {
        if (actionModal.id !== null) {
            const res = await mixDelV1(`/api/v1/be/quick-message/${actionModal.id}`, {_method: "DELETE",})
           
            if (res.status) {
                message.success(t('quick_message.delete_success'));
                getList()
            } else {
                message.error(t('quick_message.delete_failed'));
            }
        }

        handleCloseActionModal();
    };

    const openAdd = () => {
        setMode("add");
        setEditingItem(null);
        setModalOpen(true);
    };

    const openEdit = (record: TagItem) => {
        setMode("edit");
        setEditingItem(record);
        setModalOpen(true);
    };

    const handleSubmit = async (values: any) => {

        const param: TagItem = {
            title: values.title,
            is_active: values.is_active ? true : false,
            position: Number(values.position),
        };

        if (mode === "add") {
            const res = await mixPostV1('/api/v1/be/quick-message', param)

            if (res.status) {
                message.success(t('quick_message.add_success'));
                getList()
            } else {
                message.error(t('quick_message.add_failed'));
            }
        }

        if (mode === "edit" && editingItem) {

             const res = await mixPutV1(`/api/v1/be/quick-message/${editingItem.id}`, param)

            if (res.status) {
                message.success(t('quick_message.edit_success'));
                getList()
            } else {
                message.error(t('quick_message.edit_failed'));
            }
            // setData(prev =>
            //     prev.map(item =>
            //         item.id === editingItem.id ? formatted : item
            //     )
            // );
        }

        setModalOpen(false);
    };

    const filteredData = data;

    const sortedData = filteredData;

    const columns: SettingColumn<TagItem>[] = [
        {
            title: t('quick_message.col_title'),
            dataIndex: "title",
            key: "title",
            sorter: true,
        },
        {
            title: t('quick_message.col_status'),
            dataIndex: "is_active",
            key: "is_active",
            sorter: true,
            render: (is_active) =>
                is_active === true ? (
                    <Tag color="green">{t('quick_message.active')}</Tag>
                ) : (
                    <Tag color="red">{t('quick_message.inactive')}</Tag>
                ),
        },
        {
            title: t('quick_message.col_position'),
            dataIndex: "position",
            key: "position",
            sorter: true,
            render: (pos) => <Tag color="blue">#{pos}</Tag>
        },
        {
            title: t('quick_message.col_action'),
            key: "actions",
            fixed: "right",
            lockVisible: true,
            render: (_, record) => (
                <div className="flex items-center gap-2">
                    {canEdit && (
                        <button
                            onClick={() => openEdit(record)}
                            className="!text-[#3e91ab] hover:text-yellow-700 bg-yellow-50 hover:bg-yellow-100 dark:bg-yellow-900/30 dark:hover:bg-yellow-900/50 p-1.5 rounded-md transition-colors"
                        >
                            <Edit className="h-4 w-4" />
                        </button>
                    )}

                    {canDelete && (
                        <button
                            onClick={() => openDeleteModal(record.id)}
                            className="!text-red-500 hover:text-red-700 bg-red-50 hover:bg-red-100 dark:bg-red-900/30 dark:hover:bg-red-900/50 p-1.5 rounded-md transition-colors"
                        >
                            <X className="h-4 w-4" />
                        </button>
                    )}
                </div>
            ),
        },
    ];

    // Ẩn/hiện, sắp xếp, cố định, độ rộng cột — lưu theo tài khoản (extra_data)
    const {
        columns: tableColumns,
        components: tableComponents,
        tableLayout,
        scrollX,
        settingProps: columnSettingProps,
    } = useTableColumnSettings(
        "quick_message_list",
        (canEdit || canDelete) ? columns : columns.filter((c) => c.key !== "actions")
    );

    // ---------------  API------------------------
    const getList = async () => {
        setLoading(true);

        const param = {
            page,
            search: searchTerm,
            sort: sortParam,
        }

        const res = await mixGetV1('/api/v1/be/quick-messages', param)

        if (res.status) {
            setData(res.data.items)
            setTotal(res.data.total)
        }
        setLoading(false);
    }



    useEffect(() => {
        getList()
    },[page, searchTerm, sortParam])

    return (
        <Layout>
            <Helmet>
                <title>{t('page.quick_messages')} | Khothi</title>
            </Helmet>
            <div className="">
                <div className="flex gap-2 flex-col md:flex-row md:justify-between mb-4">
                    <h2 className="text-lg font-semibold t-text-white">
                        {t('quick_message.title')}
                    </h2>

                    <Space>
                        <div className="relative flex-1">
                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" className="lucide lucide-search h-5 w-5 text-gray-400" aria-hidden="true"><path d="m21 21-4.34-4.34"></path><circle cx="11" cy="11" r="8"></circle></svg>
                            </div>
                            <input
                                type="text"
                                onChange={(e) => {
                                    setSearchTerm(e.target.value)
                                    setPage(1)
                                }}
                                className="block w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md leading-5 bg-white dark:bg-gray-700 text-gray-900 t-text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-[#3e91ab] focus:border-[#3e91ab] sm:text-sm transition-colors h-[32px]"
                                placeholder={t('quick_message.search_placeholder')}
                            />
                        </div>
                        {canCreate && (
                            <Button
                                type="primary"
                                icon={<PlusOutlined />}
                                onClick={openAdd}
                                className="!bg-[#3e91ab] hover:!bg-[#357f96] border-none"
                            >
                                {t('quick_message.add')}
                            </Button>
                        )}
                        <ColumnSettingButton {...columnSettingProps} />
                    </Space>
                </div>

                <Table
                    loading={loading}
                    rowKey="id"
                    columns={tableColumns}
                    components={tableComponents}
                    tableLayout={tableLayout}
                    dataSource={sortedData}
                    scroll={{ x: scrollX }}
                    className="custom-ant-table"
                    onChange={(_pagination, _filters, sorter: any, extra: any) => {
                        // onChange của Table bắn cho cả phân trang lẫn sắp xếp. Nếu không lọc
                        // theo action thì bấm sang trang 2 sẽ bị chính handler này kéo về trang 1.
                        if (extra?.action !== 'sort') return;
                        if (sorter?.field && sorter?.order) {
                            const direction = sorter.order === 'ascend' ? 'asc' : 'desc';
                            setSortParam(`${sorter.field},${direction}`);
                        } else {
                            setSortParam('position,asc');
                        }
                        setPage(1);
                    }}
                    pagination={{
                        current: page,
                        total: total,
                        pageSize: 10,
                        onChange: (p) => setPage(p),
                    }}
                />

                <QuickModal
                    open={modalOpen}
                    mode={mode}
                    initialValues={
                        editingItem
                            ? {
                                title: editingItem.title,
                                is_active: editingItem.is_active === true,
                                position: String(editingItem.position),
                            }
                            : undefined
                    }
                    onCancel={() => setModalOpen(false)}
                    onSubmit={handleSubmit}
                />
                {/* Action Modal */}
                {actionModal.open && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
                        <div className="bg-white dark:bg-gray-800 rounded-md shadow-xl w-full max-w-md">
                            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
                                <h3 className="text-lg font-bold text-gray-900 t-text-white">
                                    {t('quick_message.delete_title')}
                                </h3>
                                <p className="text-sm text-gray-500 text-000000e0 mt-1">
                                    {t('quick_message.delete_confirm')}
                                </p>
                            </div>
                            <div className="p-6 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-3">
                                <button
                                    onClick={handleCloseActionModal}
                                    className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg !text-gray-700 dark:!text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition"
                                >
                                    {t('quick_message.cancel')}
                                </button>
                                <button
                                    onClick={handleConfirmAction}
                                    className={`px-4 py-2 !text-white rounded-lg transition ${actionModal.type === 'approve'
                                        ? 'bg-emerald-600 hover:bg-emerald-700'
                                        : 'bg-red-600 hover:bg-red-700'
                                        }`}
                                >
                                    {t('quick_message.delete')}
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </Layout>
    );
}